import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Edit2, Trash2, ExternalLink, ArrowLeft, Eye, Image as ImageIcon, X } from 'lucide-react';
import {
  adminListArticles, adminGetArticle, adminSaveArticle, adminDeleteArticle, coverSrc, formatArticleDate
} from '../site/articlesApi';
import { articlePath } from '../site/routes';
import Markdown from '../site/Markdown';
import CoinLoader from '../components/CoinLoader';

const slugify = (text) => String(text || '')
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
  .slice(0, 120);

const EMPTY = { title: '', slug: '', category: 'Guide', excerpt: '', metaDescription: '', content: '', status: 'draft', coverUrl: '' };
const MAX_COVER_BYTES = 5 * 1024 * 1024;

const inputClass = 'w-full bg-gray-950 border border-gray-700 rounded-lg px-3 py-2 text-sm sm:text-base text-white placeholder-gray-500 focus:border-yellow-500 focus:outline-none';

const Counter = ({ value, max, ideal }) => (
  <span className={`text-xs ${value.length > (ideal || max) ? 'text-orange-400' : 'text-gray-500'}`}>
    {value.length}/{ideal ? `${ideal} conseillés` : max}
  </span>
);

function SessionExpired() {
  return (
    <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4 text-sm text-orange-300">
      Votre session admin a expiré (ou date d'avant la mise en place des articles).{' '}
      <Link to="/login" className="underline font-semibold">Reconnectez-vous</Link> pour gérer les articles.
    </div>
  );
}

function ArticleEditor({ token, articleId, categories, onDone }) {
  const [form, setForm] = useState(EMPTY);
  const [slugTouched, setSlugTouched] = useState(false);
  const [coverData, setCoverData] = useState(null);
  const [preview, setPreview] = useState(false);
  const [loading, setLoading] = useState(!!articleId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!articleId) return;
    adminGetArticle(token, articleId)
      .then(a => {
        setForm({
          title: a.title, slug: a.slug, category: a.category, excerpt: a.excerpt,
          metaDescription: a.meta_description, content: a.content, status: a.status, coverUrl: a.cover_url || '',
        });
        setSlugTouched(true);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token, articleId]);

  const update = (field) => (e) => {
    const value = e.target.value;
    setForm(prev => ({
      ...prev,
      [field]: value,
      ...(field === 'title' && !slugTouched ? { slug: slugify(value) } : {}),
    }));
  };

  const handleCover = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp)$/.test(file.type)) return setError('Image PNG, JPG ou WEBP uniquement');
    if (file.size > MAX_COVER_BYTES) return setError('Image trop lourde (5 Mo max)');
    const reader = new FileReader();
    reader.onload = () => { setCoverData(reader.result); setError(''); };
    reader.readAsDataURL(file);
  };

  const removeCover = () => { setCoverData(null); setForm(prev => ({ ...prev, coverUrl: '' })); };

  const save = async (status) => {
    setSaving(true);
    setError('');
    try {
      await adminSaveArticle(token, articleId, { ...form, status, ...(coverData ? { coverData } : {}) });
      onDone();
    } catch (e) {
      setError(e.status === 401 ? 'Session expirée : reconnectez-vous.' : e.message);
      setSaving(false);
    }
  };

  if (loading) return <CoinLoader label="Chargement de l'article…" />;

  const coverPreview = coverData || coverSrc(form.coverUrl);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button onClick={onDone} className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-white">
          <ArrowLeft size={16} /> Retour aux articles
        </button>
        <button
          onClick={() => setPreview(p => !p)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-800 hover:bg-gray-700 text-sm"
        >
          {preview ? <Edit2 size={16} /> : <Eye size={16} />} {preview ? 'Modifier' : 'Aperçu'}
        </button>
      </div>

      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg px-3 py-2 text-sm">{error}</div>}

      {preview ? (
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-4 sm:p-8 max-w-3xl">
          <p className="text-xs text-yellow-400 font-semibold mb-2">{form.category}</p>
          <h1 className="text-2xl sm:text-4xl font-bold mb-3">{form.title || 'Titre de l\'article'}</h1>
          {form.excerpt && <p className="text-gray-400 mb-5">{form.excerpt}</p>}
          {coverPreview && <img src={coverPreview} alt="" className="w-full aspect-[16/9] object-cover rounded-xl mb-6" />}
          <Markdown source={form.content} />
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-4">
            <label className="block">
              <span className="block text-sm text-gray-300 mb-1">Titre *</span>
              <input value={form.title} onChange={update('title')} className={inputClass} placeholder="Comment acheter des Kamas au Maroc" />
            </label>
            <label className="block">
              <span className="flex items-center justify-between text-sm text-gray-300 mb-1">
                Contenu *
                <span className="text-xs text-gray-500">## Titre · ### Sous-titre · - liste · **gras** · [texte](/page)</span>
              </span>
              <textarea
                value={form.content}
                onChange={update('content')}
                rows={22}
                className={`${inputClass} font-mono text-sm leading-relaxed`}
                placeholder={'Introduction…\n\n## Premier titre\n\nTexte avec un [lien interne](/prix-kamas).\n\n- point 1\n- point 2'}
              />
            </label>
          </div>

          <div className="space-y-4">
            <label className="block">
              <span className="block text-sm text-gray-300 mb-1">Adresse (slug)</span>
              <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">/articles/</div>
              <input
                value={form.slug}
                onChange={(e) => { setSlugTouched(true); setForm(prev => ({ ...prev, slug: slugify(e.target.value) })); }}
                className={inputClass}
              />
            </label>
            <label className="block">
              <span className="block text-sm text-gray-300 mb-1">Catégorie</span>
              <input value={form.category} onChange={update('category')} list="article-categories" className={inputClass} />
              <datalist id="article-categories">{categories.map(c => <option key={c} value={c} />)}</datalist>
            </label>
            <label className="block">
              <span className="flex items-center justify-between text-sm text-gray-300 mb-1">Résumé <Counter value={form.excerpt} max={400} /></span>
              <textarea value={form.excerpt} onChange={update('excerpt')} rows={3} maxLength={400} className={inputClass} placeholder="Affiché sur la liste des articles" />
            </label>
            <label className="block">
              <span className="flex items-center justify-between text-sm text-gray-300 mb-1">Meta description (Google) <Counter value={form.metaDescription} max={200} ideal={160} /></span>
              <textarea value={form.metaDescription} onChange={update('metaDescription')} rows={3} maxLength={200} className={inputClass} placeholder="Texte affiché sous le titre dans Google" />
            </label>
            <div>
              <span className="block text-sm text-gray-300 mb-1">Image de couverture</span>
              {coverPreview ? (
                <div className="relative">
                  <img src={coverPreview} alt="" className="w-full aspect-[16/9] object-cover rounded-lg" />
                  <button onClick={removeCover} className="absolute top-2 right-2 p-1.5 bg-black/70 rounded-full hover:bg-black" aria-label="Retirer l'image">
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center gap-1 aspect-[16/9] border border-dashed border-gray-700 rounded-lg text-gray-500 text-sm cursor-pointer hover:border-yellow-500/50">
                  <ImageIcon size={22} />
                  Ajouter une image (PNG, JPG, WEBP · 5 Mo max)
                  <input type="file" accept="image/png,image/jpeg,image/webp" onChange={handleCover} className="hidden" />
                </label>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2 pt-2">
              <button
                disabled={saving}
                onClick={() => save('draft')}
                className="py-2.5 rounded-lg bg-gray-800 hover:bg-gray-700 text-sm font-semibold disabled:opacity-50"
              >
                Enregistrer brouillon
              </button>
              <button
                disabled={saving}
                onClick={() => save('published')}
                className="py-2.5 rounded-lg bg-yellow-500 hover:bg-yellow-400 text-black text-sm font-bold disabled:opacity-50"
              >
                {form.status === 'published' ? 'Mettre à jour' : 'Publier'}
              </button>
            </div>
            {form.status === 'published' && (
              <p className="text-xs text-gray-500">Article en ligne. « Enregistrer brouillon » le retire du site.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminArticles({ token }) {
  const [articles, setArticles] = useState(null);
  const [error, setError] = useState('');
  const [expired, setExpired] = useState(!token);
  const [editing, setEditing] = useState(null); // null = liste, 'new' ou id

  const load = useCallback(() => {
    if (!token) return;
    adminListArticles(token)
      .then(list => { setArticles(list); setError(''); })
      .catch(e => (e.status === 401 || e.status === 403 ? setExpired(true) : setError(e.message)));
  }, [token]);

  useEffect(load, [load]);

  if (expired) return <SessionExpired />;

  const categories = [...new Set((articles || []).map(a => a.category))];

  if (editing) {
    return (
      <ArticleEditor
        token={token}
        articleId={editing === 'new' ? null : editing}
        categories={categories}
        onDone={() => { setEditing(null); load(); }}
      />
    );
  }

  const handleDelete = async (article) => {
    if (!window.confirm(`Supprimer définitivement « ${article.title} » ?`)) return;
    try {
      await adminDeleteArticle(token, article.id);
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg sm:text-xl font-bold">Articles</h2>
          <p className="text-xs sm:text-sm text-gray-400">
            {articles ? `${articles.filter(a => a.status === 'published').length} publiés · ${articles.filter(a => a.status === 'draft').length} brouillons` : 'Chargement…'}
          </p>
        </div>
        <button
          onClick={() => setEditing('new')}
          className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-lg bg-yellow-500 hover:bg-yellow-400 text-black text-sm font-bold"
        >
          <Plus size={16} /> Nouvel article
        </button>
      </div>

      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg px-3 py-2 text-sm">{error}</div>}

      <div className="grid gap-3">
        {(articles || []).map(article => (
          <div key={article.id} className="bg-gray-900 border border-gray-800 rounded-xl p-3 sm:p-4 flex gap-3 items-start">
            <div className="w-20 sm:w-28 aspect-[16/9] rounded-lg overflow-hidden flex-shrink-0 bg-gradient-to-br from-yellow-500/25 to-gray-900">
              {article.cover_url && <img src={coverSrc(article.cover_url)} alt="" className="w-full h-full object-cover" />}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap mb-0.5">
                <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${article.status === 'published' ? 'bg-green-500/20 text-green-400' : 'bg-gray-700 text-gray-300'}`}>
                  {article.status === 'published' ? 'Publié' : 'Brouillon'}
                </span>
                <span className="text-xs text-gray-500">{article.category} · {formatArticleDate(article.published_at || article.updated_at)}</span>
              </div>
              <h3 className="font-semibold text-sm sm:text-base leading-snug">{article.title}</h3>
              <p className="text-xs text-gray-500 truncate">/articles/{article.slug}</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-1 flex-shrink-0">
              <button onClick={() => setEditing(article.id)} className="p-2 rounded-lg hover:bg-gray-800 text-gray-300" aria-label="Modifier" title="Modifier">
                <Edit2 size={16} />
              </button>
              {article.status === 'published' && (
                <Link to={articlePath(article.slug)} target="_blank" className="p-2 rounded-lg hover:bg-gray-800 text-gray-300" aria-label="Voir" title="Voir sur le site">
                  <ExternalLink size={16} />
                </Link>
              )}
              <button onClick={() => handleDelete(article)} className="p-2 rounded-lg hover:bg-red-500/20 text-red-400" aria-label="Supprimer" title="Supprimer">
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
        {articles && articles.length === 0 && <p className="text-gray-400 text-sm">Aucun article. Cliquez sur « Nouvel article » pour commencer.</p>}
      </div>
    </div>
  );
}
